import { useEffect } from 'react';

const FaviconUpdater = ({ color }) => {
    useEffect(() => {
        if (!color) return;

        const updateFavicon = (selectedColor) => {
            // Function to create favicon canvas for different sizes
            const createFaviconCanvas = (size) => {
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');

                // Clear background
                ctx.fillStyle = 'transparent';
                ctx.fillRect(0, 0, size, size);

                // Create a simple but elegant design
                const centerX = size / 2;
                const centerY = size / 2;
                const radius = size * 0.4;

                // Main circle with the selected color
                ctx.fillStyle = selectedColor;
                ctx.beginPath();
                ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
                ctx.fill();

                // Add a subtle inner highlight for depth
                const highlightRadius = radius * 0.6;
                const gradient = ctx.createRadialGradient(
                    centerX - radius * 0.3, centerY - radius * 0.3, 0,
                    centerX, centerY, highlightRadius
                );
                gradient.addColorStop(0, 'rgba(255, 255, 255, 0.4)');
                gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(centerX, centerY, highlightRadius, 0, 2 * Math.PI);
                ctx.fill();

                return canvas.toDataURL('image/png');
            };

            // Create and update different favicon sizes
            const sizes = [
                { size: 16, rel: 'icon', type: 'image/png', sizes: '16x16' },
                { size: 32, rel: 'icon', type: 'image/png', sizes: '32x32' },
                { size: 48, rel: 'icon', type: 'image/png', sizes: '48x48' },
                { size: 180, rel: 'apple-touch-icon', type: 'image/png', sizes: '180x180' }
            ];

            sizes.forEach(({ size, rel, type, sizes: sizeAttr }) => {
                const dataURL = createFaviconCanvas(size);

                // Remove existing favicon of this type
                const existingLinks = document.querySelectorAll(`link[rel="${rel}"][sizes="${sizeAttr}"]`);
                existingLinks.forEach(link => link.remove());

                // Create new favicon link
                const link = document.createElement('link');
                link.rel = rel;
                link.type = type;
                link.sizes = sizeAttr;
                link.href = dataURL;
                document.getElementsByTagName('head')[0].appendChild(link);
            });

            // Also create a main favicon without size specification
            const mainFavicon = createFaviconCanvas(32);
            let mainLink = document.querySelector("link[rel~='icon']:not([sizes])");
            if (!mainLink) {
                mainLink = document.createElement('link');
                mainLink.rel = 'icon';
                mainLink.type = 'image/x-icon';
                document.getElementsByTagName('head')[0].appendChild(mainLink);
            }
            mainLink.href = mainFavicon;
        };

        const updateThemeColor = (selectedColor) => {
            // Update theme-color meta tag
            let themeColorMeta = document.querySelector('meta[name="theme-color"]');
            if (!themeColorMeta) {
                themeColorMeta = document.createElement('meta');
                themeColorMeta.name = 'theme-color';
                document.getElementsByTagName('head')[0].appendChild(themeColorMeta);
            }
            themeColorMeta.content = selectedColor;

            // Update msapplication-TileColor
            let tileColorMeta = document.querySelector('meta[name="msapplication-TileColor"]');
            if (!tileColorMeta) {
                tileColorMeta = document.createElement('meta');
                tileColorMeta.name = 'msapplication-TileColor';
                document.getElementsByTagName('head')[0].appendChild(tileColorMeta);
            }
            tileColorMeta.content = selectedColor;

            // Update mask-icon color
            let maskIcon = document.querySelector('link[rel="mask-icon"]');
            if (maskIcon) {
                maskIcon.color = selectedColor;
            }

            // Update apple-mobile-web-app status bar style based on color brightness
            const isLightColor = isColorLight(selectedColor);
            let statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
            if (statusBarMeta) {
                statusBarMeta.content = isLightColor ? 'default' : 'black-translucent';
            }
        };

        const isColorLight = (color) => {
            // Convert hex to RGB
            const hex = color.replace('#', '');
            const r = parseInt(hex.substr(0, 2), 16);
            const g = parseInt(hex.substr(2, 2), 16);
            const b = parseInt(hex.substr(4, 2), 16);

            // Calculate brightness using relative luminance formula
            const brightness = (r * 299 + g * 587 + b * 114) / 1000;
            return brightness > 128;
        };

        updateFavicon(color);
        updateThemeColor(color);

        // Update document title to include current color for better UX
        const originalTitle = document.title.split(' | ')[0];
        document.title = `${originalTitle} | Current: ${color.toUpperCase()}`;

        // Cleanup function to reset title when component unmounts
        return () => {
            const originalTitle = document.title.split(' | ')[0];
            document.title = originalTitle;
        };

    }, [color]);

    // This component doesn't render anything
    return null;
};

export default FaviconUpdater;